import { baseURL } from "@/services/api";
import { IBlog, IBlogGeneric } from "global-types/global.types";
import { GetServerSideProps } from "next";
import styled from "styled-components";
import Link from "next/link";
import { NextSeo } from "next-seo";
import { SITE_NAME, SITE_URL } from "@/helper/constant";
import { useRouter } from "next/router";
import Image from "next/image";
import { removeTags } from "@/helper/helper";

interface Props {
  locale: string;
  data: IBlog<IBlogGeneric>;
}

export default function BlogDetail({ locale, data }: Props) {
  const { asPath } = useRouter();

  return (
    <>
      <Wrapper>
        <NextSeo
          title={`${data.data.meta_title || data.data.name} | ${SITE_NAME}`}
          // @ts-ignore
          description={
            data?.data?.meta_des ||
            removeTags(data?.data?.description).substring(0, 160)
          }
          canonical={SITE_URL + "/" + locale + asPath}
          additionalMetaTags={[
            {
              name: "title",
              content: `${
                data?.data?.meta_title || data?.data?.name
              } | ${SITE_NAME}`,
            },
          ]}
          openGraph={{
            url: SITE_URL + "/" + locale + asPath,
            title: `${
              data?.data?.meta_title || data?.data?.name
            } | ${SITE_NAME}`,
            // @ts-ignore
            description:
              data?.data?.meta_des ||
              removeTags(data?.data?.description).substring(0, 300),
            images: [
              {
                // @ts-ignore
                url: data?.data?.og_img,
              },
            ],
            siteName: SITE_NAME,
          }}
        />
        <div className="blogInside">
          <div className="blog-inside-content">
            <div className="blog-inside-header">
              <div className="breadcrumb">
                <Link href={`/${locale}`}>Ana səhifə</Link>
                <span className="breadcrumb-seperator" />
                <Link href={`/${locale}/blog`}>Blog</Link>
                <span className="breadcrumb-seperator" />
                <Link href={`/${locale}/blog/${data.data.slug}`}>
                  {data.data.name}
                </Link>
              </div>
              <Image
                width={400}
                height={400}
                src={`${data.data.thumbnail}`}
                alt={data.data.meta_title ? data.data.meta_title : ""}
              />
            </div>
            <div className="blog-inside-container">
              <h1>{data.data.name}</h1>
              <div className="blog-inside-detail">
                <span>{data.data.created_at}</span>
              </div>
              <div
                dangerouslySetInnerHTML={{
                  __html: data.data.description,
                }}
              />
            </div>
          </div>
        </div>
      </Wrapper>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { locale } = ctx;
  const { slug } = ctx.params!;

  const blogDetail = await fetch(baseURL + `/blog/${slug}`, {
    headers: {
      "x-localization": locale ? locale : "az",
    },
  });

  return {
    props: {
      data: await blogDetail.json(),
      locale,
    },
  };
};

const Wrapper = styled.div`
  .blogInside {
    width: 1289px;
    margin: 0 auto;
    padding-bottom: 7rem;

    @media screen and (max-width: 1199px) {
      width: 100%;
      padding: 0 16px;
    }

    .breadcrumb {
      display: none;
    }

    .blog-inside-content {
      margin-top: 36px;

      .blog-inside-header {
        width: 100%;
        height: 27rem;
        position: relative;
        box-sizing: border-box;
        border-radius: 6px;
        overflow: hidden;
        margin-bottom: 36px;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: top;
        }

        .breadcrumb {
          position: absolute;
          top: 24px;
          left: 24px;
          display: flex;
          padding: 0;

          a {
            color: #fff;
          }

          .breadcrumb-seperator {
            background-color: #fff;
          }
        }
      }

      .blog-inside-container {
        width: 68%;
        margin: auto;
        margin-bottom: 5.6rem;

        h1 {
          font-style: normal;
          font-weight: 600;
          font-size: 36px;
          line-height: 150%;
          color: #040941;
          margin-bottom: 24px;
        }

        p {
          font-style: normal;
          font-weight: 300;
          font-size: 20px;
          line-height: 30px;
          color: #4f576c;
        }
      }
    }

    .blog-content {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      column-gap: 16px;
      row-gap: 36px;
      margin-top: 3.6rem;
    }

    .blog-inside-detail {
      display: flex;
      align-items: center;
      margin-bottom: 24px;

      span {
        font-style: normal;
        font-weight: 300;
        font-size: 14px;
        line-height: 100%;
        color: #666c7b;
        margin-right: 32px;
      }

      div {
        font-style: normal;
        font-weight: 300;
        font-size: 14px;
        line-height: 100%;
        color: #666c7b;
        display: flex;

        img {
          margin-left: 6px;
          width: 17px;
          height: 10px;
          object-fit: contain;
        }
      }
    }

    @media screen and (max-width: 767.9px) {
      .breadcrumb {
        display: block;
      }

      .blog-inside-content {
        .blog-inside-header {
          margin-bottom: 24px;

          .breadcrumb {
            display: none !important;
          }
        }

        .blog-inside-container {
          width: 100%;

          h4 {
            font-size: 24px;
          }

          p {
            font-size: 16px;
          }
        }
      }

      .blog-content {
        grid-template-columns: repeat(1, 1fr);
      }
    }
  }
`;
